import { useTheme } from "next-themes";
import { Link, useNavigate } from "react-router-dom";
import {
  ExternalLink, Gauge, LayoutDashboard, LayoutGrid, ListOrdered, LogOut, Moon, Package, PauseCircle, Settings, Shield, Store, Sun, Users,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { SpendMeter } from "./SpendMeter";

// The /admin area for staff and up (the dashboard for admin and owner), plus the same pages inside Shopify admin.
const ADMIN_PAGES = [["orders", "Orders", Package], ["queue", "Queue", ListOrdered], ["spend-gate", "Spend gate", Gauge], ["held", "Held", PauseCircle],
  ["accounts", "Accounts", Users]];
const TEAM = ["staff", "admin", "owner"];
const ITEM = "gap-2 cursor-pointer";

// Same rule as the server's roles.mask_email: first letter, then ***@domain.
export const maskEmail = (email) => {
  const e = (email || "").trim().toLowerCase();
  const at = e.indexOf("@");
  return at > -1 && e.slice(at + 1) ? `${e.slice(0, 1)}***@${e.slice(at + 1)}` : "***";
};

export const initials = (user) => {
  const base = (user?.name || user?.email || "?").split("@")[0];
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
};

const AdminMenu = ({ user, scheme }) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger data-testid="account-admin-menu" className={ITEM}>
      <Shield className="h-4 w-4" /> Admin
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className={`w-52 ${scheme}`}>
      {["admin", "owner"].includes(user.role) && (
        <DropdownMenuItem asChild className={ITEM}>
          <Link to="/admin" data-testid="account-admin-dashboard"><LayoutDashboard className="h-4 w-4" /> Dashboard</Link>
        </DropdownMenuItem>
      )}
      {ADMIN_PAGES.map(([path, label, Icon]) => (
        <DropdownMenuItem key={path} asChild className={ITEM}>
          <Link to={`/admin/${path}`} data-testid={`account-admin-${path}`}><Icon className="h-4 w-4" /> {label}</Link>
        </DropdownMenuItem>
      ))}
      {user.admin_app_url && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className={ITEM}>
            <a href={user.admin_app_url} target="_blank" rel="noopener noreferrer" data-testid="account-admin-shopify">
              <ExternalLink className="h-4 w-4" /> In Shopify admin
            </a>
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

const ThemeItem = () => {
  const { theme, setTheme } = useTheme();
  const dark = theme === "dark";
  return (
    <DropdownMenuItem data-testid="account-theme-toggle" className={ITEM} onSelect={(e) => { e.preventDefault(); setTheme(dark ? "light" : "dark"); }}>
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {dark ? "Light mode" : "Dark mode"}
    </DropdownMenuItem>
  );
};

const TRIGGER = {
  light: "h-10 w-10 border border-border bg-accent text-accent-foreground hover:opacity-90 focus-visible:ring-offset-background",
  dark: "h-9 w-9 bg-primary font-heading text-primary-foreground hover:brightness-110 focus-visible:ring-offset-[hsl(220_18%_11%)]",
};

// One account menu for every header: the studio Navbar (light) and the dark /admin, /admin/accounts and /partner headers.
export const AccountMenu = ({ testid = "account-menu-btn", variant = "light", onOpenGallery, className = "" }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const masked = maskEmail(user.email);
  const scheme = variant === "dark" ? "dark" : "";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={testid} aria-label={`Account menu (${masked})`} title={masked}
          className={`grid shrink-0 place-items-center rounded-full text-sm font-bold transition-[opacity,filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${TRIGGER[variant]} ${className}`}>
          {initials(user)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className={`w-60 ${scheme}`}>
        <DropdownMenuLabel className="min-w-0">
          {user.name && <span data-testid="account-name" className="block truncate">{user.name}</span>}
          <span data-testid="account-email" className="block truncate font-normal text-muted-foreground">{masked}</span>
        </DropdownMenuLabel>
        <SpendMeter />
        <DropdownMenuSeparator />
        {onOpenGallery && (
          <DropdownMenuItem data-testid="account-my-ducks" onSelect={onOpenGallery} className={ITEM}><LayoutGrid className="h-4 w-4" /> My Ducks</DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className={ITEM}>
          <Link to="/account" data-testid="account-settings-link"><Settings className="h-4 w-4" /> Account settings</Link>
        </DropdownMenuItem>
        {user.role === "partner" && (
          <DropdownMenuItem asChild className={ITEM}>
            <Link to="/partner" data-testid="account-partner-orders"><Store className="h-4 w-4" /> Your orders &amp; sales</Link>
          </DropdownMenuItem>
        )}
        {TEAM.includes(user.role) && <AdminMenu user={user} scheme={scheme} />}
        <ThemeItem />
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="account-signout" onSelect={() => signOut(() => navigate("/login", { replace: true }))}
          className={`${ITEM} text-rose-600 focus:text-rose-700 dark:text-rose-300 dark:focus:text-rose-200`}>
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
